import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface DuplicateContact {
  id: string;
  first_name?: string;
  last_name?: string;
  primary_phone?: string;
  primary_email?: string;
  created_at?: string;
  // assigned_sales_consultant_id removido
  metadata?: any;
  date_of_birth?: string;
  primary_address?: string;
  deals_count?: number;
  leads_count?: number;
  messages_count?: number;
  calls_count?: number;
  created_by?: string;
}

interface DuplicateGroup {
  phone: string;
  contacts: DuplicateContact[];
}

type MergeStrategy = 'keep_oldest' | 'keep_newest' | 'keep_most_complete' | 'manual';

export function useMergeContacts() {
  const [loading, setLoading] = useState(false);
  const [duplicates, setDuplicates] = useState<DuplicateGroup[]>([]);

  async function findDuplicates() {
    try {
      setLoading(true);
      
      // Buscar todos os contatos com telefone válido
      const { data: contacts, error } = await supabase
        .from('contacts')
        .select(`
          id, 
          first_name, 
          last_name, 
          primary_phone, 
          primary_email, 
          created_at, 
          metadata,
          date_of_birth,
          primary_address,
          created_by
        `)
        .not('primary_phone', 'is', null)
        .neq('primary_phone', '')
        .neq('primary_phone', 'null')
        .neq('primary_phone', 'undefined')
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Buscar contagem de relacionamentos para cada contato
      const contactIds = contacts?.map(c => c.id) || [];
      
      const [dealsData, messagesData, callsData] = await Promise.all([
        supabase.from('deals').select('contact_id').in('contact_id', contactIds),
        // DEPRECATED - leads query removed
        supabase.from('messages').select('contact_id').in('contact_id', contactIds),
        supabase.from('calls').select('contact_id').in('contact_id', contactIds),
      ]);

      // Criar mapa de contagens
      const counts = contactIds.reduce((acc, id) => {
        acc[id] = {
          deals: dealsData.data?.filter(d => d.contact_id === id).length || 0,
          leads: 0, // DEPRECATED - leads table no longer used
          messages: messagesData.data?.filter(m => m.contact_id === id).length || 0,
          calls: callsData.data?.filter(c => c.contact_id === id).length || 0,
        };
        return acc;
      }, {} as Record<string, any>);

      // Agrupar por telefone
      const phoneMap = new Map<string, DuplicateContact[]>();
      
      contacts?.forEach((contact) => {
        const phone = contact.primary_phone!;
        if (!phoneMap.has(phone)) {
          phoneMap.set(phone, []);
        }
        phoneMap.get(phone)!.push({
          ...contact,
          deals_count: counts[contact.id]?.deals || 0,
          leads_count: counts[contact.id]?.leads || 0,
          messages_count: counts[contact.id]?.messages || 0,
          calls_count: counts[contact.id]?.calls || 0,
        });
      });

      // Filtrar apenas grupos com duplicatas
      const duplicateGroups: DuplicateGroup[] = [];
      phoneMap.forEach((contacts, phone) => {
        if (contacts.length > 1) {
          duplicateGroups.push({ phone, contacts });
        }
      });

      setDuplicates(duplicateGroups);
      return duplicateGroups;
    } catch (err) {
      console.error('Error finding duplicates:', err);
      toast.error('Erro ao buscar duplicatas');
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function mergeContacts(keepId: string, mergeIds: string[]) {
    try {
      setLoading(true);

      // 1. Atualizar todas as referências para apontar para o contato principal
      const tables = [
        'calls',
        'deals',
        'leads',
        'messages',
        'notes',
        'tasks',
        'case_contacts',
        'consent_records',
        'contacts_marketing_data'
      ];

      for (const table of tables) {
        for (const mergeId of mergeIds) {
          await supabase
            .from(table as any)
            .update({ contact_id: keepId })
            .eq('contact_id', mergeId);
        }
      }

      // 2. Copiar emails e telefones adicionais do contato duplicado
      for (const mergeId of mergeIds) {
        // Copiar emails
        const { data: emails } = await supabase
          .from('contact_emails')
          .select('*')
          .eq('contact_id', mergeId);

        if (emails && emails.length > 0) {
          for (const email of emails) {
            // Verificar se já existe antes de copiar
            const { data: existing } = await supabase
              .from('contact_emails')
              .select('id')
              .eq('contact_id', keepId)
              .eq('email', email.email)
              .maybeSingle();

            if (!existing) {
              await supabase
                .from('contact_emails')
                .insert({
                  contact_id: keepId,
                  email: email.email,
                  label: email.label,
                  is_primary: false
                });
            }
          }
        }

        // Copiar telefones
        const { data: phones } = await supabase
          .from('contact_phones')
          .select('*')
          .eq('contact_id', mergeId);

        if (phones && phones.length > 0) {
          for (const phone of phones) {
            const { data: existing } = await supabase
              .from('contact_phones')
              .select('id')
              .eq('contact_id', keepId)
              .eq('number_e164', phone.number_e164)
              .maybeSingle();

            if (!existing) {
              await supabase
                .from('contact_phones')
                .insert({
                  contact_id: keepId,
                  number_e164: phone.number_e164,
                  country_code: phone.country_code,
                  label: phone.label,
                  is_primary: false
                });
            }
          }
        }

        // Copiar endereços
        const { data: addresses } = await supabase
          .from('contact_addresses')
          .select('*')
          .eq('contact_id', mergeId);

        if (addresses && addresses.length > 0) {
          for (const address of addresses) {
            await supabase
              .from('contact_addresses')
              .insert({
                contact_id: keepId,
                type: address.type,
                line1: address.line1,
                line2: address.line2,
                city: address.city,
                state: address.state,
                postal_code: address.postal_code,
                country_code: address.country_code,
                is_primary: false
              });
          }
        }
      }

      // 3. Deletar os contatos duplicados
      const { error: deleteError } = await supabase
        .from('contacts')
        .delete()
        .in('id', mergeIds);

      if (deleteError) throw deleteError;

      toast.success(`${mergeIds.length} contato(s) mesclado(s) com sucesso`);
      
      // Atualizar lista de duplicatas
      await findDuplicates();
      
      return true;
    } catch (err: any) {
      console.error('Error merging contacts:', err);
      toast.error(`Erro ao mesclar contatos: ${err.message}`);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  function selectContactToKeep(contacts: DuplicateContact[], strategy: MergeStrategy): string {
    if (strategy === 'keep_oldest') {
      return contacts.sort((a, b) => 
        new Date(a.created_at!).getTime() - new Date(b.created_at!).getTime()
      )[0].id;
    }
    
    if (strategy === 'keep_newest') {
      return contacts.sort((a, b) => 
        new Date(b.created_at!).getTime() - new Date(a.created_at!).getTime()
      )[0].id;
    }
    
    if (strategy === 'keep_most_complete') {
      return contacts.sort((a, b) => {
        const scoreA = calculateCompletenessScore(a);
        const scoreB = calculateCompletenessScore(b);
        return scoreB - scoreA;
      })[0].id;
    }
    
    return contacts[0].id; // fallback
  }

  function calculateCompletenessScore(contact: DuplicateContact): number {
    let score = 0;
    if (contact.first_name) score += 1;
    if (contact.last_name) score += 1;
    if (contact.primary_email) score += 2;
    if (contact.primary_phone) score += 2;
    if (contact.date_of_birth) score += 1;
    if (contact.primary_address) score += 1;
    if (contact.deals_count && contact.deals_count > 0) score += 3;
    if (contact.leads_count && contact.leads_count > 0) score += 2;
    if (contact.messages_count && contact.messages_count > 0) score += 1;
    if (contact.calls_count && contact.calls_count > 0) score += 1;
    return score;
  }

  async function mergeAllDuplicates(
    strategy: MergeStrategy,
    groupsToMerge?: DuplicateGroup[],
    onProgress?: (current: number, total: number) => void
  ) {
    const groups = groupsToMerge || duplicates;
    const total = groups.length;
    let successful = 0;
    let failed = 0;

    try {
      setLoading(true);

      for (let i = 0; i < groups.length; i++) {
        const group = groups[i];
        
        try {
          const keepId = selectContactToKeep(group.contacts, strategy);
          const mergeIds = group.contacts.filter(c => c.id !== keepId).map(c => c.id);
          
          await mergeContacts(keepId, mergeIds);
          successful++;
          
          if (onProgress) {
            onProgress(i + 1, total);
          }
        } catch (err) {
          console.error(`Error merging group ${i}:`, err);
          failed++;
        }
      }

      toast.success(`Merge concluído: ${successful} grupos mesclados, ${failed} falharam`);
      await findDuplicates(); // Refresh
      
      return { successful, failed, total };
    } catch (err: any) {
      console.error('Error in bulk merge:', err);
      toast.error(`Erro no merge em massa: ${err.message}`);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function createBackup() {
    // Nota: Backup deve ser feito manualmente antes de executar merge em massa
    // Sugerimos exportar dados importantes ou usar snapshots do Supabase
    toast.success('Lembre-se: faça backup manual dos dados antes do merge em massa');
    return true;
  }

  return {
    loading,
    duplicates,
    findDuplicates,
    mergeContacts,
    mergeAllDuplicates,
    createBackup,
    selectContactToKeep,
  };
}
